import React, { forwardRef, useImperativeHandle, useRef } from 'react'
import { Virtuoso, VirtuosoHandle } from 'react-virtuoso'
import { Box } from '@fundamentals'
import { useScrollContainer } from '@common/ScrollContainerContext'
import { useVirtuosoScroll } from '@common/hooks/useVirtuosoScroll'
import { QuestionGroupProps } from '@core/components/templateAnswersForm'
import { ITemplateQuestionGroup } from '@core/entities/template/TemplateQuestionGroup'
import { QuestionGroup } from './QuestionGroup'

export type VirtualizedQuestionGroupListHandle = {
  scrollToQuestionGroup: (groupIndex: number) => void
}

type VirtualizedQuestionGroupListProps = Omit<
  QuestionGroupProps,
  'groupIndex' | 'questionGroup'
> & {
  questionGroups: Array<ITemplateQuestionGroup>
}

// Shared Virtuoso renderer for editable question forms, review stages and
// read-only submitted answers. The nearest ScrollContainerProvider determines
// whether the list follows an application scroller or the document window.
export const VirtualizedQuestionGroupList = forwardRef<
  VirtualizedQuestionGroupListHandle,
  VirtualizedQuestionGroupListProps
>(({ questionGroups, ...questionGroupProps }, ref) => {
  const scrollContainer = useScrollContainer()
  const virtuosoRef = useRef<VirtuosoHandle>(null)
  const scrollParent = scrollContainer ?? undefined
  const { scrollToIndex } = useVirtuosoScroll({
    virtuosoRef,
    rows: questionGroups,
    scope: 'questions',
    scrollParent,
    registerForE2E: scrollContainer !== null,
  })

  useImperativeHandle(
    ref,
    () => ({
      scrollToQuestionGroup: scrollToIndex,
    }),
    [scrollToIndex],
  )

  if (scrollContainer === null) {
    return null
  }

  return (
    <Virtuoso
      ref={virtuosoRef}
      useWindowScroll={scrollContainer === undefined}
      customScrollParent={scrollParent}
      data={questionGroups}
      computeItemKey={(_, questionGroup) => questionGroup.id}
      // Keep ~1 viewport of extra content mounted in each direction
      // (typically 1-2 question groups) so media-heavy rows load and decode
      // offscreen instead of popping in at the viewport edge mid-scroll.
      increaseViewportBy={{ top: 1000, bottom: 1000 }}
      itemContent={(groupIndex, questionGroup) => (
        // Row spacing lives inside the row: margins on the row root collapse
        // through Virtuoso's measuring wrapper and cause scroll jitter.
        <Box data-virtuoso-row-id={questionGroup.id} sx={{ pb: 2 }}>
          <QuestionGroup
            {...questionGroupProps}
            questionGroup={questionGroup}
            groupIndex={groupIndex}
            sx={{ mb: 0 }}
          />
        </Box>
      )}
    />
  )
})

VirtualizedQuestionGroupList.displayName = 'VirtualizedQuestionGroupList'
